import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getSupplier, Supplier, InventoryItem, ReceivingInvoice } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const SupplierDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { sessionToken } = useAuth();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [invoices, setInvoices] = useState<ReceivingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    getSupplier(sessionToken, parseInt(id))
      .then(data => {
        setSupplier(data.supplier);
        setItems(data.items as any);
        setInvoices(data.invoices as any);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, id]);

  if (loading) return <div className="inv2-loading">Loading...</div>;
  if (error) return <div className="inv2-error">{error}</div>;
  if (!supplier) return <div className="inv2-error">Supplier not found</div>;

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <div>
          <Link to="/inventory/suppliers" className="inv2-back-link">← Back to Suppliers</Link>
          <h1>{supplier.name}</h1>
          {!supplier.is_active && <span className="inv2-badge-inactive">Inactive</span>}
        </div>
      </div>

      <div className="inv2-detail-grid">
        <div className="inv2-detail-section">
          <h3>Contact Information</h3>
          <dl className="inv2-dl">
            <dt>Contact Person</dt><dd>{supplier.contact_name || '—'}</dd>
            <dt>Phone</dt><dd>{supplier.phone || '—'}</dd>
            <dt>Email</dt><dd>{supplier.email || '—'}</dd>
            <dt>Address</dt><dd>{supplier.address || '—'}</dd>
            <dt>Website</dt><dd>{supplier.website ? <a href={supplier.website} target="_blank" rel="noreferrer">{supplier.website}</a> : '—'}</dd>
            <dt>Notes</dt><dd>{supplier.notes || '—'}</dd>
          </dl>
        </div>
      </div>

      {/* Supplier's Items */}
      <div className="inv2-section">
        <h2>Inventory Items ({items.length})</h2>
        <div className="inv2-table-container">
          <DataTable
            rows={items}
            rowKey={(item: any) => item.id}
            caption="Items"
            empty="No items"
            columns={[
              { key: 'item', header: 'Item', priority: 'primary', render: (item: any) => <Link to={`/inventory/items/${item.id}`} className="inv2-link">{item.name}</Link> },
              { key: 'onhand', header: 'On Hand', priority: 'summary', numeric: true, render: (item: any) => `${item.on_hand_quantity} ${item.base_unit}` },
              { key: 'status', header: 'Status', priority: 'summary', render: (item: any) => <span className={`inv2-status inv2-status-${item.status}`}>{item.status}</span> },
            ]}
          />
        </div>
      </div>

      {/* Receiving History */}
      <div className="inv2-section">
        <h2>Receiving History ({invoices.length})</h2>
        <div className="inv2-table-container">
          <DataTable
            rows={invoices}
            rowKey={(inv: any) => inv.id}
            caption="Invoices"
            empty="No invoices"
            columns={[
              { key: 'invoice', header: 'Invoice', priority: 'primary', render: (inv: any) => <Link to={`/inventory/receiving/${inv.id}`} className="inv2-link">{inv.invoice_number || `#${inv.id}`}</Link> },
              { key: 'total', header: 'Total', priority: 'summary', numeric: true, render: (inv: any) => (inv.total != null ? `$${Number(inv.total).toFixed(2)}` : '—') },
              { key: 'date', header: 'Date', priority: 'summary', render: (inv: any) => (inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—') },
              { key: 'status', header: 'Status', priority: 'summary', render: (inv: any) => <span className={`inv2-status inv2-status-${inv.status}`}>{inv.status}</span> },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default SupplierDetail;
